import qs from "query-string";
import isArray from "lodash/isArray";

import { BEConfig } from "../config/env";
import { generateUUID } from "../util";
import { createPromiseCachedFunction } from "../util/common";

import { webapiGet, webapiPost, webapiPut } from "../webapis/core";

import ApiService from "./apiService";

class CommonService extends ApiService {
  async unsubscribeEmail(data: { customer_uid: string; lang: string }) {
    const { common, emailUnsubscribe } = BEConfig.commonApi;
    const apiURL = `${this.getBaseUrl()}${common}${emailUnsubscribe}`;

    const response = await webapiPost(
      this.getAccessToken(),
      apiURL,
      data,
      this.getVisitorId() || generateUUID()
    ).request;

    return response.data;
  }

  async getCountryList(language) {
    const { settingsHandle } = BEConfig.commonApi;

    const apiURL = `${this.getBaseUrl()}${settingsHandle}`;
    const queryParams = qs.stringify({
      language
    });
    const endpoint = `${apiURL}?${queryParams}`;

    const response = await webapiGet(
      this.getAccessToken(),
      endpoint,
      generateUUID()
    ).request;

    //@ts-ignore
    if (!isArray(response.data)) throw new Error(response);

    return response.data;
  }

  public getShortCountryList = createPromiseCachedFunction(
    async (language: string): Promise<ShortCountryListItem[]> => {
      const { settingsHandle, shortCountryList } = BEConfig.commonApi;

      const apiURL = `${this.getBaseUrl()}${settingsHandle}${shortCountryList}`;
      const queryParams = qs.stringify({
        language
      });
      const endpoint = `${apiURL}?${queryParams}`;

      const response = await webapiGet(
        this.getAccessToken(),
        endpoint,
        generateUUID()
      ).request;
      if (!isArray(response.data)) throw new Error(response.data);

      return response.data;
    },
    {
      key: language => language,
      ms: 5 * 10000
    }
  );

  async getCountriesMobileDetails() {
    const { settingsHandle, mobileDetails } = BEConfig.commonApi;
    const apiURL = `${this.getBaseUrl()}${settingsHandle}`;
    const endpoint = `${apiURL}${mobileDetails}`;
    const response = await webapiGet(this.getAccessToken(), endpoint).request;

    //@ts-ignore
    if (!isArray(response.data)) throw new Error(response);

    return response.data;
  }

  async getAddressDetails(shortAddress: string) {
    const { addressDetailsHandle } = BEConfig.commonApi;
    const apiURL = `${this.getBaseUrl()}${addressDetailsHandle}`;
    const endpoint = `${apiURL}?shortaddress=${shortAddress}`;
    const response = await webapiGet(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId() || generateUUID()
    ).request;

    return response.data;
  }

  async getNationalAddressMandatoryStatus() {
    const { nationalAddressMandatoryStatusHandle } = BEConfig.commonApi;
    const apiURL = `${this.getBaseUrl()}${nationalAddressMandatoryStatusHandle}`;
    const response = await webapiGet(
      this.getAccessToken(),
      apiURL,
      this.getVisitorId() || generateUUID()
    ).request;

    return { ...response.data };
  }

  async getPayLaterOptionsList() {
    const { settingsHandle, payLaterOptions } = BEConfig.commonApi;

    const apiURL = `${this.getBaseUrl()}${settingsHandle}${payLaterOptions}`;
    const response = await webapiGet(this.getAccessToken(), apiURL).request;

    return response.data;
  }

  async createVideoRecord(
    data: CreateVideoRecord
  ): Promise<CreateVideoRecordPayload> {
    const { videoRecordHandle } = BEConfig.commonApi;

    const apiURL = `${this.getBaseUrl()}${videoRecordHandle}`;
    const response = await webapiPost(
      this.getAccessToken(),
      apiURL,
      { ...data },
      this.getVisitorId()
    ).request;

    return response.data;
  }

  async getVideoRecord(videoId: string): Promise<GetVideoRecordPayload> {
    const { videoRecordWatched } = BEConfig.commonApi;

    const apiURL = `${this.getBaseUrl()}${videoRecordWatched}/${videoId}`;
    const response = await webapiGet(this.getAccessToken(), apiURL).request;

    return response.data;
  }

  async getExpressDeliveryTimeline(params?: Record<string, string>) {
    const { settingsHandle, countryByIdSetting, deliveryTimeline } =
      BEConfig.commonApi;

    const apiURL = `${this.getBaseUrl()}${settingsHandle}`;
    let endpoint = `${apiURL}${countryByIdSetting}${deliveryTimeline}`;

    if (params && Object.keys(params).length) {
      endpoint = `${endpoint}?${new URLSearchParams(params).toString()}`;
    }

    const response = await webapiGet(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId() || generateUUID()
    ).request;

    //@ts-ignore
    if (!isArray(response.data)) throw new Error(response);

    return response.data;
  }

  async putExpressDeliveryTimeline(body, params?: Record<string, string>) {
    const { settingsHandle, countryByIdSetting, deliveryTimeline } =
      BEConfig.commonApi;

    const apiURL = `${this.getBaseUrl()}${settingsHandle}`;
    let endpoint = `${apiURL}${countryByIdSetting}${deliveryTimeline}`;

    if (params && Object.keys(params).length) {
      endpoint = `${endpoint}?${new URLSearchParams(params).toString()}`;
    }

    const response = await webapiPut(
      this.getAccessToken(),
      endpoint,
      body,
      this.getVisitorId() || generateUUID()
    ).request;

    //@ts-ignore
    if (!isArray(response.data)) throw new Error(response);

    return response.data;
  }

  getBaseUrl() {
    const { baseURL, protocol, port, versionInfo } = BEConfig.commonApi;

    const url = `${protocol}${baseURL}${port}${versionInfo}`;

    return url;
  }
}

type GetVideoRecordPayload = {
  videoId: string;
  isWatched: boolean;
  message?: string;
};

type CreateVideoRecordPayload = {
  name: string;
  mobileNumber: string;
  email: string;
  videoId: string;
  customerId?: number;
  visitorId?: string;
  message?: string;
  _id?: string;
  __v?: number;
};

export type CreateVideoRecord = {
  name: string;
  mobileNumber: string;
  email: string;
  videoId: string;
};

export interface ShortCountryListItem {
  countryCode: string;
  countryId: number;
  countryName: string;
  countrySHORT: string;
  currencyCode: string;
  defaultCountryName?: string;
  en_countryName: string;
  freeShippingUpto?: number;
  mobileCountryCode: number;
  mobileLocalCode: string;
  mobileLocalNumberLength: number;
  mobileNumberCriteria?: {
    acceptableLength: number[];
  };
  paymentOptions?: Array<{
    defaultValue?: string;
    image: string;
    key: string;
    name: string;
  }>;
}

const instance = new CommonService();

export default instance;
